import React from "react";
import styled from "styled-components";
import Text from "../../atoms/Text";
import Button from "../../atoms/Button";
import Tag from "../../atoms/Tag";
import { AIReviewFeedback, FEEDBACK_ITEMS, FeedbackItem, getPromptTypeKoreanName, UserRole } from "../../../common/models/types";
import { ColorSystem } from "../../../components/design/design-system";

const TableContainer = styled.div`
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: 100%;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

const Thead = styled.thead`
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f9fafb;
  }
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  color: #374151;
  white-space: nowrap;

  &:nth-child(1) { width: 5%; }   /* 번호 */
  &:nth-child(2) { width: 6%; }   /* 리뷰ID */
  &:nth-child(3) { width: 7%; }   /* 환자번호 */
  &:nth-child(4) { width: 8%; }   /* 분석종류 */
  &:nth-child(5) { width: 12%; }  /* 사용자 */
  &:nth-child(6) { width: 8%; }   /* 별점 */
  &:nth-child(7) { width: 14%; }  /* 좋았던점 */
  &:nth-child(8) { width: 14%; }  /* 아쉬운점 */
  &:nth-child(9) { width: 12%; }  /* 코멘트 */
  &:nth-child(10) { width: 8%; }  /* 피드백일 */
  &:nth-child(11) { width: 6%; }  /* 액션 */
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const TagsWrapper = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`;

const CommentCell = styled.div`
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;

  &:hover {
    white-space: normal;
    word-break: break-word;
  }
`;

const EmptyState = styled.div`
  padding: 60px 20px;
  text-align: center;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 20px;
  border-top: 1px solid #e5e7eb;
`;

const PageButton = styled.button<{ active?: boolean }>`
  padding: 6px 12px;
  border: 1px solid ${(props) => (props.active ? "#4A90E2" : "#e5e7eb")};
  background: ${(props) => (props.active ? "#4A90E2" : "#fff")};
  color: ${(props) => (props.active ? "#fff" : "#374151")};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover:not(:disabled) {
    border-color: #4a90e2;
    color: ${(props) => (props.active ? "#fff" : "#4A90E2")};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const PageInfo = styled(Text)`
  margin: 0 16px;
  font-size: 14px;
  color: #6b7280;
`;

interface AdminFeedbackTableProps {
  feedbacks: AIReviewFeedback[];
  loading: boolean;
  currentPage: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onViewDetail: (reviewId: number) => void;
}

// 별점을 이모지로 변환
const renderRating = (rating: number): string => {
  if (rating === 0) return "-";
  return "⭐".repeat(rating);
};

// Premium Role 배지 표시
const getPremiumRoleBadge = (
  premiumRole: string | null | undefined
): { label: string; color: ColorSystem } | null => {
  if (!premiumRole) return null;

  if (premiumRole === "유료프리미엄") {
    return { label: "유료 프리미엄", color: "Red" as ColorSystem };
  }

  if (premiumRole === UserRole.ADMIN || premiumRole === "admin") {
    return { label: "ADM", color: "Red" as ColorSystem };
  }

  if (premiumRole.startsWith(UserRole.GYEONGGI_PHARMACIST) || premiumRole.startsWith("경기도약사회")) {
    return { label: "경기도", color: "Blue" as ColorSystem };
  }

  if (premiumRole.startsWith(UserRole.SEOUL_PHARMACIST) || premiumRole.startsWith("서울시약사회")) {
    return { label: "서울시", color: "Purple" as ColorSystem };
  }

  if (premiumRole.startsWith(UserRole.INCHEON_PHARMACIST) || premiumRole.startsWith("인천시약사회")) {
    return { label: "인천시", color: "Skyblue" as ColorSystem };
  }

  if (premiumRole === "Premium") {
    return { label: "Premium", color: "Orange" as ColorSystem };
  }

  return { label: premiumRole, color: "Purple" as ColorSystem };
};

// 좋았던 점을 한글로 변환 (Blue)
const renderLikes = (items: FeedbackItem[] | null): React.ReactNode => {
  if (!items || items.length === 0) return "-";

  return (
    <TagsWrapper>
      {items.map((item) => (
        <Tag key={item} variant="Tinted" color="Blue" size="Small">
          {FEEDBACK_ITEMS[item] || item}
        </Tag>
      ))}
    </TagsWrapper>
  );
};

// 아쉬운 점을 한글로 변환 (Red)
const renderDislikes = (items: FeedbackItem[] | null): React.ReactNode => {
  if (!items || items.length === 0) return "-";

  return (
    <TagsWrapper>
      {items.map((item) => (
        <Tag key={item} variant="Tinted" color="Red" size="Small">
          {FEEDBACK_ITEMS[item] || item}
        </Tag>
      ))}
    </TagsWrapper>
  );
};

export const AdminFeedbackTable: React.FC<AdminFeedbackTableProps> = ({
  feedbacks,
  loading,
  currentPage,
  pageSize,
  total,
  onPageChange,
  onViewDetail,
}) => {
  const totalPages = Math.ceil(total / pageSize);

  // 페이지 번호 생성
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  if (loading && feedbacks.length === 0) {
    return (
      <TableContainer>
        <EmptyState>
          <Text>데이터를 불러오는 중...</Text>
        </EmptyState>
      </TableContainer>
    );
  }

  if (feedbacks.length === 0) {
    return (
      <TableContainer>
        <EmptyState>
          <Text>조회된 피드백이 없습니다.</Text>
        </EmptyState>
      </TableContainer>
    );
  }

  return (
    <TableContainer>
      <Table>
        <Thead>
          <Tr>
            <Th>번호</Th>
            <Th>리뷰ID</Th>
            <Th>환자번호</Th>
            <Th>분석종류</Th>
            <Th>사용자</Th>
            <Th>별점</Th>
            <Th>좋았던점</Th>
            <Th>아쉬운점</Th>
            <Th>코멘트</Th>
            <Th>피드백일</Th>
            <Th>액션</Th>
          </Tr>
        </Thead>
        <Tbody>
          {feedbacks.map((feedback) => {
            const aiReview = feedback.aiReview;
            const user = feedback.user; // 사용자 정보는 feedback.user에 있음
            const patientId = aiReview?.request?.patientId || aiReview?.patientId; // patientId는 request 안에 있음

            return (
              <Tr key={feedback.id}>
                <Td>{feedback.id}</Td>
                <Td>{feedback.aiReviewId}</Td>
                <Td>{patientId || "-"}</Td>
                <Td>
                  <Tag variant="Tinted" color="Blue" size="Small">
                    {getPromptTypeKoreanName(aiReview?.promptType)}
                  </Tag>
                </Td>
                <Td>
                  <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    {user?.name && (
                      <Text textType="Caption" color="#6b7280">
                        {user.name}
                      </Text>
                    )}
                  </div>
                </Td>
                <Td>{renderRating(feedback.rating)}</Td>
                <Td>{renderLikes(feedback.likes)}</Td>
                <Td>{renderDislikes(feedback.dislikes)}</Td>
                <Td>
                  <CommentCell title={feedback.comment || undefined}>
                    {feedback.comment || "-"}
                  </CommentCell>
                </Td>
                <Td>{new Date(feedback.updatedAt).toLocaleDateString("ko-KR")}</Td>
                <Td>
                  <ActionButtons>
                    <Button
                      size="Small"
                      type="Text"
                      variant="Contained"
                      color="Primary"
                      onClick={() => onViewDetail(feedback.aiReviewId)}
                    >
                      상세
                    </Button>
                  </ActionButtons>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>

      {totalPages > 1 && (
        <PaginationContainer>
          <PageButton onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
            이전
          </PageButton>

          {getPageNumbers().map((page) => (
            <PageButton key={page} active={page === currentPage} onClick={() => onPageChange(page)}>
              {page}
            </PageButton>
          ))}

          <PageInfo>
            {currentPage} / {totalPages} 페이지
          </PageInfo>

          <PageButton
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            다음
          </PageButton>
        </PaginationContainer>
      )}
    </TableContainer>
  );
};
